import { redactText } from "@fabric-harness/sdk";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

export interface DatabricksRawProtocolClientOptions {
  host: string;
  token: string | (() => string | Promise<string>);
  fetchImpl?: typeof fetch;
  /**
   * Retries after the initial request for transient failures. Default 3.
   * Applied automatically only to reads and mutations explicitly classified as safe.
   */
  maxRetries?: number;
  /** Base backoff in ms (exponential per attempt; `Retry-After` honored when present). Default 500. */
  retryBaseDelayMs?: number;
  /** Abort signal forwarded to every request. */
  signal?: AbortSignal;
  /**
   * Extra headers resolved per attempt — e.g. on-behalf-of propagation
   * (forwarding a user's access token or workspace OBO headers) without
   * rebuilding the client. Never logged.
   */
  extraHeaders?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);
}

export interface DatabricksRawProtocolRequestOptions {
  /** Explicitly classify a mutation. `always` must only be used for idempotent operations. */
  retry?: "safe" | "always" | "never";
  /** Per-operation retry count override. */
  maxRetries?: number;
  /** Per-operation cancellation, combined with the client-wide signal when both are present. */
  signal?: AbortSignal;
  /** Expected response media type for streaming protocols. */
  accept?: "text/event-stream";
}

function isRetryableStatus(status: number): boolean {
  // Databricks model-service listings can transiently return 499 under load.
  return status === 429 || status === 499 || (status >= 500 && status <= 599);
}

function retryDelayMs(response: Response, attempt: number, base: number): number {
  const retryAfter = response.headers.get("retry-after");
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) return seconds * 1000;
  }
  return base * 2 ** attempt;
}

interface DatabricksRawProtocolRule {
  readonly id: string;
  readonly methods: readonly string[];
  readonly path: RegExp;
}

/**
 * Exhaustive SDK-gap allowlist. A raw request is denied before authentication or network I/O unless
 * its method and path match one of these reviewed protocols. Additions require an SDK coverage audit,
 * a boundary test, and compatibility documentation.
 */
const DATABRICKS_RAW_PROTOCOL_RULES: readonly DatabricksRawProtocolRule[] = [
  {
    id: "workspace-objects",
    methods: ["GET", "POST"],
    path: /^\/api\/2\.0\/workspace\/(?:delete|export|get-status|import|list|mkdirs)$/u,
  },
  {
    id: "mlflow-traces-v3",
    methods: ["POST"],
    path: /^\/api\/3\.0\/mlflow\/traces$/u,
  },
  {
    id: "ai-gateway-discovery",
    methods: ["GET"],
    path: /^\/api\/(?:ai-gateway\/v2\/endpoints|2\.1\/unity-catalog\/model-(?:provider-)?services)$/u,
  },
  {
    id: "sql-warehouse-discovery",
    methods: ["GET"],
    path: /^\/api\/2\.0\/sql\/warehouses$/u,
  },
  {
    id: "agent-services-beta",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    path: /^\/api\/2\.1\/unity-catalog\/(?:agent-services(?:\/[^/?#]+)?|permissions\/AGENT_SERVICE\/[^/?#]+)$/u,
  },
  {
    id: "managed-memory-beta",
    methods: ["GET", "POST", "PATCH", "DELETE"],
    path: /^\/api\/2\.1\/unity-catalog\/memory-stores(?:\/[^/?#]+(?:\/entries(?::(?:get|search))?)?)?$/u,
  },
  {
    id: "genie-agent-mode-beta",
    methods: ["GET", "POST"],
    path: /^\/api\/2\.0\/genie\/agents\/[^/?#]+\/(?:responses|conversations\/[^/?#]+\/items)$/u,
  },
  {
    id: "genie-query-result-sdk-compatibility",
    methods: ["GET"],
    path: /^\/api\/2\.0\/genie\/spaces\/[^/?#]+\/conversations\/[^/?#]+\/messages\/[^/?#]+\/attachments\/[^/?#]+\/query-result$/u,
  },
  {
    id: "vector-search-query-sdk-compatibility",
    methods: ["POST"],
    path: /^\/api\/2\.0\/vector-search\/indexes\/[^/?#]+\/query$/u,
  },
  {
    id: "responses-agent-custom-schema",
    methods: ["POST"],
    path: /^\/serving-endpoints\/[^/?#]+\/invocations$/u,
  },
] as const;

function assertRawProtocolAllowed(method: string, path: string): void {
  const normalizedMethod = method.toUpperCase();
  if (!path.startsWith("/") || path.includes("?") || path.includes("#")) {
    throw new Error(
      `databricks raw protocol denied: ${normalizedMethod} ${path}; pass a relative path and structured query parameters.`,
    );
  }
  const rule = DATABRICKS_RAW_PROTOCOL_RULES.find(
    (candidate) => candidate.methods.includes(normalizedMethod) && candidate.path.test(path),
  );
  if (!rule) {
    throw new Error(
      `databricks raw protocol denied: ${normalizedMethod} ${path}; use an official @databricks/sdk-* service client.`,
    );
  }
}

/**
 * Quarantined raw transport for reviewed protocols that are absent from the modular Databricks SDK.
 * Stable APIs must use a generated `@databricks/sdk-*` service client instead.
 */
export class DatabricksRawProtocolClient {
  private readonly host: string;
  private readonly token: DatabricksRawProtocolClientOptions["token"];
  private readonly fetchImpl: typeof fetch;
  private readonly maxRetries: number;
  private readonly retryBaseDelayMs: number;
  private readonly signal: AbortSignal | undefined;
  private readonly extraHeaders: DatabricksRawProtocolClientOptions["extraHeaders"];

  constructor(options: DatabricksRawProtocolClientOptions) {
    this.host = databricksWorkspaceOrigin(options.host);
    this.token = options.token;
    this.fetchImpl = options.fetchImpl ?? fetch;
    this.maxRetries = options.maxRetries ?? 3;
    this.retryBaseDelayMs = options.retryBaseDelayMs ?? 500;
    this.signal = options.signal;
    this.extraHeaders = options.extraHeaders;
  }

  get<T>(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
    options?: DatabricksRawProtocolRequestOptions,
  ): Promise<T> {
    return this.requestJson<T>("GET", path, undefined, query, options);
  }

  post<T>(
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>,
    options?: DatabricksRawProtocolRequestOptions,
  ): Promise<T> {
    return this.requestJson<T>("POST", path, body, query, options);
  }

  patch<T>(
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>,
    options?: DatabricksRawProtocolRequestOptions,
  ): Promise<T> {
    return this.requestJson<T>("PATCH", path, body, query, options);
  }

  delete<T>(
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>,
    options?: DatabricksRawProtocolRequestOptions,
  ): Promise<T> {
    return this.requestJson<T>("DELETE", path, body, query, options);
  }

  private async requestJson<T>(
    method: string,
    path: string,
    body?: unknown,
    query: Record<string, string | number | boolean | undefined> = {},
    options: DatabricksRawProtocolRequestOptions = {},
  ): Promise<T> {
    const response = await this.send(method, path, body, query, options);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  /**
   * Perform an authenticated request while leaving a successful response body unread. This is
   * intended for bounded binary downloads and streaming APIs such as Genie Agent Mode.
   */
  postRaw(
    path: string,
    body?: unknown,
    query: Record<string, string | number | boolean | undefined> = {},
    options: DatabricksRawProtocolRequestOptions = {},
  ): Promise<Response> {
    return this.send("POST", path, body, query, options);
  }

  private async send(
    method: string,
    path: string,
    body: unknown,
    query: Record<string, string | number | boolean | undefined>,
    options: DatabricksRawProtocolRequestOptions,
  ): Promise<Response> {
    assertRawProtocolAllowed(method, path);
    const url = new URL(`${this.host}${path.startsWith("/") ? path : `/${path}`}`);
    for (const [key, value] of Object.entries(query))
      if (value !== undefined) url.searchParams.set(key, String(value));
    const canRetry =
      options.retry === "always" ||
      (options.retry !== "never" &&
        (options.retry === "safe" || method === "GET" || method === "HEAD"));
    const maxRetries = canRetry ? (options.maxRetries ?? this.maxRetries) : 0;
    const signal = combineAbortSignals(this.signal, options.signal);

    for (let attempt = 0; ; attempt += 1) {
      // Resolve the (possibly rotating) token on every attempt.
      const token = typeof this.token === "function" ? await this.token() : this.token;
      const extra =
        typeof this.extraHeaders === "function" ? await this.extraHeaders() : this.extraHeaders;
      const init: RequestInit = {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(options.accept ? { accept: options.accept } : {}),
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
          ...extra,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        ...(signal ? { signal } : {}),
      };

      let response: Response;
      try {
        response = await this.fetchImpl(url, init);
      } catch (error) {
        // Network error — retry transient failures, but never swallow an abort.
        if (signal?.aborted || attempt >= maxRetries) throw error;
        await abortableSleep(this.retryBaseDelayMs * 2 ** attempt, signal);
        continue;
      }

      if (response.ok) {
        return response;
      }

      if (isRetryableStatus(response.status) && attempt < maxRetries) {
        await response.body?.cancel();
        await abortableSleep(retryDelayMs(response, attempt, this.retryBaseDelayMs), signal);
        continue;
      }

      const text = redactText(await response.text(), { additionalSecrets: [token] });
      throw new Error(
        `databricks: ${response.status} ${response.statusText}: ${text.slice(0, 2000)}`,
      );
    }
  }
}

function combineAbortSignals(
  clientSignal?: AbortSignal,
  requestSignal?: AbortSignal,
): AbortSignal | undefined {
  if (!clientSignal) return requestSignal;
  if (!requestSignal || requestSignal === clientSignal) return clientSignal;
  return AbortSignal.any([clientSignal, requestSignal]);
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted)
    return Promise.reject(
      signal.reason ?? new DOMException("The operation was aborted.", "AbortError"),
    );
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException("The operation was aborted.", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}
